import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Store do Live Update — fonte única de verdade pra:
 *  - Banner amarelo "Atualizar agora" (lê pendingUpdate)
 *  - Tela /admin/live-update (lê tudo)
 *  - Indicador no footer (lê pendingUpdate)
 *
 * Persistido em localStorage pra sobreviver a reloads do WebView. Log fica em
 * memória (com cap de 50 eventos) — não vale a pena persistir log, ocupa espaço
 * e o operador só consulta quando tem problema acontecendo agora.
 */

export type CheckResult = 'idle' | 'up_to_date' | 'newer' | 'error'
export type LogLevel = 'info' | 'warn'

export interface LogEvent {
  ts: number
  level: LogLevel
  msg: string
}

export interface PendingUpdate {
  version: string
  id: string
}

interface LiveUpdateState {
  currentVersion: string | null
  lastCheckAt: number | null
  lastCheckResult: CheckResult
  lastError: string | null
  pendingUpdate: PendingUpdate | null
  eventLog: LogEvent[]

  setCurrent: (v: string | null) => void
  startCheck: () => void
  finishCheck: (result: CheckResult, error?: string | null) => void
  setPending: (p: PendingUpdate | null) => void
  pushEvent: (level: LogLevel, msg: string) => void
  clearLog: () => void
}

const LOG_CAP = 50

export const useLiveUpdateStore = create<LiveUpdateState>()(
  persist(
    (set) => ({
      currentVersion: null,
      lastCheckAt: null,
      lastCheckResult: 'idle',
      lastError: null,
      pendingUpdate: null,
      eventLog: [],

      setCurrent: (v) => set({ currentVersion: v }),
      startCheck: () => set({ lastCheckAt: Date.now(), lastError: null }),
      finishCheck: (result, error = null) =>
        set({ lastCheckResult: result, lastError: error }),
      setPending: (p) => set({ pendingUpdate: p }),
      pushEvent: (level, msg) =>
        set((s) => ({
          eventLog: [{ ts: Date.now(), level, msg }, ...s.eventLog].slice(0, LOG_CAP),
        })),
      clearLog: () => set({ eventLog: [] }),
    }),
    {
      name: 'live-update-state',
      // Só persiste o essencial — log fica em memória.
      partialize: (s) => ({
        currentVersion: s.currentVersion,
        lastCheckAt: s.lastCheckAt,
        lastCheckResult: s.lastCheckResult,
        lastError: s.lastError,
        pendingUpdate: s.pendingUpdate,
      }),
    },
  ),
)
